// (c) chaehongee. Confidential and proprietary.

import dagre from 'dagre'
import type { Edge, Node, Position } from '@xyflow/react'
import type { SessionInfoResponse } from 'models-ts/session'
import type { CommitResponse } from 'models-ts/commit'
import type { NodeResponse } from 'models-ts/node'
import { TREE_LAYOUT_CONSTANTS } from './constants'
type LayoutDirection = 'LR' | 'TB'

export function buildGraph(session: SessionInfoResponse): { nodes: Node[]; edges: Edge[] } {
    const g = new dagre.graphlib.Graph()
    g.setGraph({
        rankdir: 'TB' as LayoutDirection,
        ranksep: TREE_LAYOUT_CONSTANTS.RANKSEP,
        nodesep: TREE_LAYOUT_CONSTANTS.NODESEP,
        edgesep: TREE_LAYOUT_CONSTANTS.EDGESEP
    })
    g.setDefaultEdgeLabel(() => ({}))

    const nodeById = new Map<string, NodeResponse>(session.nodes.map(n => [n.id, n]))

    // Build a quick lookup of node_to_id -> commit (for prompt metadata)
    const commitByNodeTo = new Map<string, CommitResponse>()
    for (const c of session.commits as any[]) {
        commitByNodeTo.set((c as any).node_to_id, c as any)
    }

    const rfNodes: Node[] = session.nodes.map(n => {
        const commit = commitByNodeTo.get(n.id)
        const meta = (commit as any)?.commit_metadata as Record<string, any> | undefined
        const prompt =
            (meta?.input && (meta.input.prompt || meta.input.text)) ||
            (meta?.latest_params && (meta.latest_params.prompt || meta.latest_params.text)) ||
            undefined
        return {
            id: n.id,
            type: 'image',
            data: { id: n.id, image_path: n.image_path, type: n.type, label: prompt },
            position: { x: 0, y: 0 },
            width: TREE_LAYOUT_CONSTANTS.NODE_SIZE,
            height: TREE_LAYOUT_CONSTANTS.NODE_SIZE,
        }
    })

    rfNodes.forEach(n => g.setNode(n.id, { width: TREE_LAYOUT_CONSTANTS.NODE_SIZE, height: TREE_LAYOUT_CONSTANTS.NODE_SIZE }))

    const rfEdges: Edge[] = []
    for (const c of session.commits) {
        const target = c.node_to_id
        const sources = (c as any).nodes_from_ids as string[] | undefined
        if (!sources || sources.length === 0) continue
        for (const s of sources) {
            if (!nodeById.has(s) || !nodeById.has(target)) continue
            const id = `${c.id}:${s}->${target}`
            rfEdges.push({ id, source: s, target, type: 'custom', animated: false })
            g.setEdge(s, target)
        }
    }

    dagre.layout(g)
    rfNodes.forEach(n => {
        const pos = g.node(n.id)
        if (pos) {
            n.position = { x: pos.x - TREE_LAYOUT_CONSTANTS.NODE_SIZE / 2, y: pos.y - TREE_LAYOUT_CONSTANTS.NODE_SIZE / 2 }
                ; (n as any).sourcePosition = 'bottom' as Position
                ; (n as any).targetPosition = 'top' as Position
        }
    })

    return { nodes: rfNodes, edges: rfEdges }
}


